import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Input,
  Spinner,
  Switch,
  Text,
} from '@chakra-ui/react';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { NativeSelectRoot, NativeSelectField } from '@/components/ui/native-select';
import { Cadence, Course, CourseReminders } from '@/lib/types';

const CADENCES: Array<{ value: Cadence; label: string }> = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

function formatStopDate(date: string): string {
  // 'YYYY-MM-DD' parsed as local calendar date — new Date(str) would shift it
  // a day west of Greenwich.
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// Admin reminder cadence editor: one nudge per module walked down the season
// calendar. Cadence is a course setting, not part of a version — re-timing a
// season never cuts a version or touches anyone's progress.
export default function RemindersTab({ course }: { course: Course }) {
  const [reminders, setReminders] = useState<CourseReminders | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    api
      .get(`courses/${course.id}/reminders`)
      .then((res) => setReminders(unwrap<CourseReminders>(res)))
      .catch(() => setReminders(null))
      .finally(() => setIsLoading(false));
  }, [course.id]);

  const patch = (changes: Partial<CourseReminders>) =>
    setReminders((prev) => (prev ? { ...prev, ...changes } : prev));

  const save = async () => {
    if (!reminders) return;
    setIsSaving(true);
    try {
      const response = await api.put(`courses/${course.id}/reminders`, {
        enabled: reminders.enabled,
        season_start: reminders.season_start ?? '',
        cadence: reminders.cadence,
        due_date: reminders.due_date ?? '',
      });
      setReminders(unwrap<CourseReminders>(response));
      toaster.create({
        title: 'Cadence saved',
        description: "Reminders go out on the new schedule. Nobody's progress changed.",
        type: 'success',
      });
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to save the cadence',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size='lg' />
      </Center>
    );
  }

  if (!reminders) {
    return (
      <Text fontSize='sm' color='fg.muted' textAlign='center' py={8}>
        Failed to load reminder settings.
      </Text>
    );
  }

  const isOpenAccess = course.access_mode === 'open';

  return (
    <Grid templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) 300px' }} gap={5} alignItems='start'>
      <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={6}>
        <Flex align='flex-start' justify='space-between' gap={5} mb={reminders.enabled ? 5 : 0}>
          <Box>
            <Text
              fontFamily='heading'
              fontSize='2xs'
              fontWeight='700'
              textTransform='uppercase'
              letterSpacing='0.16em'
              color='fg.muted'
              mb={1}
            >
              Reminder cadence
            </Text>
            <Text fontSize='sm' color='fg.muted' maxW='lg'>
              One nudge per module, on this season&apos;s calendar. Reminders never lock content —
              a learner can always work ahead.
            </Text>
            {isOpenAccess && (
              <Text fontSize='sm' color='fg.subtle' mt={2}>
                This course uses an open link, so there is nobody to notify — switch access to
                log-in required to enable reminders.
              </Text>
            )}
          </Box>
          <Switch.Root
            checked={reminders.enabled}
            disabled={isOpenAccess}
            onCheckedChange={(e) => patch({ enabled: e.checked })}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
        </Flex>

        {reminders.enabled && (
          <>
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
              gap={4}
              pb={5}
              borderBottomWidth='1px'
              borderColor='border'
            >
              <Field label='Season start'>
                <Input
                  type='date'
                  value={reminders.season_start ?? ''}
                  onChange={(e) => patch({ season_start: e.target.value || null })}
                />
              </Field>
              <Field label='Cadence'>
                <NativeSelectRoot>
                  <NativeSelectField
                    value={reminders.cadence}
                    onChange={(e) => patch({ cadence: e.target.value as Cadence })}
                  >
                    {CADENCES.map((cadence) => (
                      <option key={cadence.value} value={cadence.value}>
                        {cadence.label}
                      </option>
                    ))}
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
              <Field
                label='Course due date'
                helperText='Optional — the date the whole course should be finished by.'
              >
                <Input
                  type='date'
                  value={reminders.due_date ?? ''}
                  onChange={(e) => patch({ due_date: e.target.value || null })}
                />
              </Field>
            </Grid>

            {(reminders.schedule ?? []).length > 0 && (
              <Box mt={4}>
                {(reminders.schedule ?? []).map((stop) => (
                  <Grid
                    key={stop.module_id}
                    templateColumns='72px minmax(0,1fr) auto'
                    gap={3}
                    alignItems='center'
                    py={2.5}
                    borderBottomWidth='1px'
                    borderColor='border'
                  >
                    <Text
                      fontFamily='heading'
                      fontSize='2xs'
                      fontWeight='700'
                      textTransform='uppercase'
                      letterSpacing='0.08em'
                      color={stop.passed ? 'fg.subtle' : 'brand.fg'}
                    >
                      Week {stop.position}
                    </Text>
                    <Text fontSize='sm' color='fg' truncate>
                      {stop.name || 'Untitled module'}
                    </Text>
                    <Text fontSize='sm' color='fg.muted'>
                      {formatStopDate(stop.date)}
                      {stop.passed && ' · sent'}
                    </Text>
                  </Grid>
                ))}
              </Box>
            )}

            {reminders.due_date && (
              <Box
                bg='brand.soft'
                borderWidth='1px'
                borderColor='brand.emphasized'
                borderRadius='md'
                px={4}
                py={3}
                mt={4}
              >
                <Text fontSize='sm' color='fg'>
                  Learners see {formatStopDate(reminders.due_date)} on their course card, and
                  anyone unfinished after it shows as overdue in Tracking.
                </Text>
              </Box>
            )}
          </>
        )}

        <Flex align='center' gap={4} mt={5} wrap='wrap'>
          <Button variant='primary' size='sm' onClick={save} loading={isSaving} disabled={isOpenAccess}>
            Save cadence
          </Button>
          <Text fontSize='sm' color='fg.muted'>
            Changing the cadence doesn&apos;t create a new version or reset anyone&apos;s progress.
          </Text>
        </Flex>
      </Box>

      <Box bg='bg.panel' borderWidth='1px' borderColor='border' borderRadius='lg' shadow='sm' p={5}>
        <Text
          fontFamily='heading'
          fontSize='2xs'
          fontWeight='700'
          textTransform='uppercase'
          letterSpacing='0.16em'
          color='fg.muted'
          mb={2.5}
        >
          Re-using this course
        </Text>
        <Text fontSize='sm' color='fg.muted' mb={2.5}>
          Cadence lives with the course as a setting, not with a version — re-time it each season
          and leave the content alone.
        </Text>
        <Text fontSize='sm' color='fg.muted' mb={2.5}>
          Each reminder is a one-time nudge on its date. A missed one doesn&apos;t re-fire or
          escalate.
        </Text>
        <Text fontSize='sm' color='fg.muted'>
          Reminders need log-in access and an assigned audience — there&apos;s nobody to notify on
          an open, anonymous link.
        </Text>
      </Box>
    </Grid>
  );
}
